/**
 * Shared search helpers.
 *
 * The search index is generated at build time (`scripts/generate-search-index.ts`)
 * and consumed by `useSearchIndex()` in the browser. Both sides must tokenize
 * identically, so the tokenizer lives here and is imported by both.
 */

export interface SearchIndexTopic {
	/** slug — link target `/topic/<slug>` */
	s: string
	/** display name */
	n: string
}

export interface SearchIndexAuthor {
	/** slug — link target `/author/<slug>` */
	s: string
	/** display name */
	n: string
	/** job title / institution line */
	t?: string
	/** avatar image */
	i?: string
	ins?: string
}

export interface SearchIndexArticle {
	/** slug */
	s: string
	/** title */
	t: string
	/** plain-text teaser */
	e?: string
	/** image */
	i?: string
	/** publish date */
	d?: string
	/** author slugs */
	a?: string[]
	/** topic slugs */
	p?: string[]
	/** `article` or `blog` */
	k?: 'article' | 'blog'
}

export interface SearchIndexFile {
	v: number
	generatedAt: string
	topics: SearchIndexTopic[]
	authors: SearchIndexAuthor[]
	articles: SearchIndexArticle[]
	/** token → delta/base36 encoded article positions */
	index: Record<string, string>
}

/** Words too common to be useful as a search term (and too large to index). */
const STOPWORDS = new Set([
	'a', 'an', 'and', 'are', 'as', 'at', 'be', 'been', 'but', 'by', 'can', 'could',
	'did', 'do', 'does', 'for', 'from', 'had', 'has', 'have', 'he', 'her', 'hers',
	'him', 'his', 'how', 'i', 'if', 'in', 'into', 'is', 'it', 'its', 'me', 'more',
	'most', 'my', 'no', 'not', 'of', 'on', 'one', 'only', 'or', 'other', 'our',
	'out', 'she', 'should', 'so', 'some', 'such', 'than', 'that', 'the', 'their',
	'them', 'then', 'there', 'these', 'they', 'this', 'those', 'to', 'too', 'up',
	'us', 'was', 'we', 'were', 'what', 'when', 'where', 'which', 'while', 'who',
	'whom', 'why', 'will', 'with', 'would', 'you', 'your', 'yours',
])

const TOKEN_PATTERN = /[\p{L}\p{N}]+/gu

/** Lowercase word tokens; single characters and punctuation are dropped. */
export function tokenizeSearch(text: string | null | undefined): string[] {
	if (!text) return []
	return (String(text).toLowerCase().match(TOKEN_PATTERN) ?? []).filter(token => token.length > 1)
}

/**
 * Tokens used to match a user query. Stopwords are dropped when the query has
 * something more meaningful, but kept when the query is *only* stopwords.
 */
export function queryTokens(query: string): string[] {
	const all = tokenizeSearch(query)
	const meaningful = all.filter(token => !STOPWORDS.has(token))
	return meaningful.length ? meaningful : all
}

export function isStopword(token: string): boolean {
	return STOPWORDS.has(token)
}

const BASE36 = '0123456789abcdefghijklmnopqrstuvwxyz'

function toBase36(value: number): string {
	if (value <= 0) return '0'
	let out = ''
	let n = value
	while (n > 0) {
		out = BASE36[n % 36] + out
		n = Math.floor(n / 36)
	}
	return out
}

function fromBase36(value: string): number {
	let n = 0
	for (const char of value) {
		const digit = BASE36.indexOf(char)
		if (digit < 0) return Number.NaN
		n = n * 36 + digit
	}
	return n
}

/** Encode ascending positions as gap-encoded base36 — compact and cheap to decode. */
export function encodePostings(positions: number[]): string {
	// Gap encoding only round-trips when positions are ascending, so normalise here
	// rather than trusting every caller to remember.
	const ascending = [...new Set(positions)].sort((a, b) => a - b)
	let previous = -1
	const parts: string[] = []
	for (const position of ascending) {
		parts.push(toBase36(position - previous - 1))
		previous = position
	}
	return parts.join(',')
}

export function decodePostings(encoded: string): number[] {
	if (!encoded) return []
	const out: number[] = []
	let previous = -1
	for (const part of encoded.split(',')) {
		const gap = fromBase36(part)
		if (!Number.isFinite(gap)) continue
		previous += gap + 1
		out.push(previous)
	}
	return out
}

function escapeRegExp(value: string): string {
	return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const HTML_ESCAPES: Record<string, string> = {
	'&': '&amp;',
	'<': '&lt;',
	'>': '&gt;',
	'"': '&quot;',
	'\'': '&#39;',
}

export function escapeHtml(value: string): string {
	return value.replace(/[&<>"']/g, char => HTML_ESCAPES[char] ?? char)
}

/** Escape `text` and wrap every query token in `<strong>` so matches stand out. */
export function highlightMatches(text: string | null | undefined, tokens: string[]): string {
	if (!text) return ''
	const escaped = escapeHtml(text)
	const usable = [...new Set(tokens.filter(token => token.length > 0))]
		.sort((a, b) => b.length - a.length)
	if (!usable.length) return escaped
	const pattern = usable.map(escapeRegExp).join('|')
	try {
		return escaped.replace(new RegExp(`(${pattern})`, 'giu'), '<strong>$1</strong>')
	} catch {
		return escaped
	}
}
